export type CapitalGlobalCoverageBenefitOption = {
  code: string;
  /** ProductElementId do benefício no InsureMO (quando numérico). */
  productElementId?: number;
  /** Nome exibido no seletor (ex.: "R$ 5.000"). */
  name: string;
  /**
   * Capital em reais derivado do nome da opção. `null` quando o nome não é um
   * valor monetário (ex.: "30 Diárias de Incapacidade Temporária").
   */
  capital: number | null;
};

export type CapitalGlobalCoverageCatalogItem = {
  id: string;
  code: string;
  /** Numeric product element ID used in the attach-element payload. */
  productElementId: number;
  title: string;
  description: string;
  /** Required coverage: switch always on and locked. */
  required: boolean;
  /** InsureMO SumInsuredMin when HasSI === 'Y'; null → no product floor. */
  limitMin: number | null;
  /** InsureMO SumInsuredMax when HasSI === 'Y'; null → fallback to Morte/reference ceiling. */
  limitMax: number | null;
  /** `true` quando exclusiva de sócio (`GBLPartnerOnlyCoverages`). */
  partnerOnly: boolean;
  /** Código da cobertura-base proporcional (`ReferenceCoverageCode`). */
  referenceCoverageCode?: string;
  /** Código da cobertura pareada para dependência ou exclusividade. */
  parentCoverageCode?: string;
  /** `RelationType`: '1' = Dependente de, '2' = Exclusiva com, '3' = Nenhuma. */
  relationType?: string;
  /** Grupo de exclusão mútua que atravessa tipos — ver `exclusion-group.util`. */
  exclusionGroupCode?: string;
  /**
   * Teto do campo `%` em relação à Morte (InsureMO `Proportion`).
   * Ausente → default 100%.
   */
  proportion?: number;
  /**
   * Categoria InsureMO (`CoverageCategory_CodeDesc` ou código).
   * Coberturas de auxílio usam verba absoluta fixa entre grupos escalonados.
   */
  category?: string;
  /** `SumInsuredDefinition` do InsureMO (ex.: 'DECLARED', 'BENEFIT_SELECTION'). */
  sumInsuredDefinition?: string;
  /**
   * Opções de benefício da própria cobertura (ex.: Auxílio Funeral com níveis
   * R$ 5.000/7.000/10.000). Presente → o capital é ESCOLHIDO, não digitado.
   */
  benefitOptions?: CapitalGlobalCoverageBenefitOption[];
};

export type CapitalGlobalAssistanceCatalogItem = {
  code: string;
  title: string;
  description: string;
  /** `true` quando exclusiva de sócio (`GBLPartnerOnlyCoverages`). */
  partnerOnly: boolean;
  /** Grupo de exclusão mútua que atravessa tipos — ver `exclusion-group.util`. */
  exclusionGroupCode?: string;
};

export type CapitalGlobalSelectOption = {
  value: string;
  label: string;
  /** `true` quando exclusiva de sócio (`GBLPartnerOnlyCoverages`). */
  partnerOnly: boolean;
  /** Grupo de exclusão mútua que atravessa tipos — ver `exclusion-group.util`. */
  exclusionGroupCode?: string;
};

/** Benefit with a populated groupCode — rendered in the "Assistência Funeral" select. */
export function isGroupedAssistanceBenefit(benefit: {
  groupCode: string | null;
}): boolean {
  return Boolean(benefit.groupCode?.trim());
}

/** Benefit without groupCode — rendered as a standalone toggle. */
export function isStandaloneAssistanceBenefit(benefit: {
  groupCode: string | null;
}): boolean {
  return !isGroupedAssistanceBenefit(benefit);
}

/**
 * Finds the "Morte" coverage from the catalog by name.
 * Identification is done at runtime via name match to avoid hardcoding product codes.
 */
export function findMorteCoverage(
  catalog: CapitalGlobalCoverageCatalogItem[]
): CapitalGlobalCoverageCatalogItem | undefined {
  return catalog.find(c => c.title === 'Morte');
}

/** Morte is always enabled and locked at 100% of the entity/group reference capital. */
export function isMorteCoverageId(
  coverageId: string,
  morteId: string | undefined
): boolean {
  return morteId != null && coverageId === morteId;
}

/** As coberturas básicas usam o capital-base do risco, não outra cobertura. */
export function isCapitalGlobalBasicCoverage(
  item: Pick<CapitalGlobalCoverageCatalogItem, 'title'>
): boolean {
  return item.title === 'Morte' || item.title === 'Morte Acidental';
}

/** Código InsureMO da categoria Auxílio no Capital Global. */
export const AUXILIO_COVERAGE_CATEGORY_CODE = '7';

function normalizeCategoryLabel(category: string): string {
  return category.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * Cobertura cujas opções TRAZEM o capital no nome — ex.: Auxílio/Assistência
 * Funeral com níveis "R$ 5.000/7.000/10.000". Serve para as regras ancoradas
 * em valor (acoplamento de nível entre dependente e pai).
 *
 * `BENEFIT_SELECTION_WITH_SI` fica de fora: lá a opção não carrega valor em R$
 * (ex.: "30 Diárias") e o SI é resolvido pelo InsureMO a partir do benefício.
 * Para saber se a linha usa seletor, use `usesBenefitSelection`.
 */
export function hasBenefitCapitalOptions(
  item: Pick<
    CapitalGlobalCoverageCatalogItem,
    'sumInsuredDefinition' | 'benefitOptions'
  >
): boolean {
  if (item.sumInsuredDefinition === 'BENEFIT_SELECTION_WITH_SI') return false;
  return (item.benefitOptions ?? []).some(option => option.capital != null);
}

/**
 * Cobertura contratada pela ESCOLHA de um benefício — o corretor seleciona a
 * opção e o attach envia `benefits: [...]` na cobertura (mesmo canal que o
 * guarda-chuva de Assistência/Capitalização já usa). O seletor substitui o
 * input livre de capital e trava o %, que passa a ser derivado.
 *
 * Vale para os dois `sumInsuredDefinition` com opções — `BENEFIT_SELECTION` e
 * `BENEFIT_SELECTION_WITH_SI` — e para os dois formatos de opção:
 * - nível em R$ (ex.: Assistência Funeral Individual "R$ 5.000") → a opção
 *   também define o capital da linha;
 * - opção sem valor monetário (ex.: "30 Diárias de Incapacidade Temporária")
 *   → a opção escolhe o número de diárias e o `%` vira o input do valor da
 *   diária (ver `usesBenefitSelectionWithPercent`).
 */
export function usesBenefitSelection(
  item: Pick<CapitalGlobalCoverageCatalogItem, 'benefitOptions'>
): boolean {
  return (item.benefitOptions ?? []).length > 0;
}

/**
 * Cobertura de diárias (`BENEFIT_SELECTION_WITH_SI`): a opção escolhe o número
 * de diárias ("30 Diárias…") e o `%` é o INPUT do valor da diária —
 * diária = % do capital individual, limitada ao teto (`limitMax`/`proportion`).
 * Diferente das demais seleções de benefício, o campo `%` fica editável e o
 * capital da linha é derivado dele, não da opção.
 */
export function usesBenefitSelectionWithPercent(
  item: Pick<
    CapitalGlobalCoverageCatalogItem,
    'sumInsuredDefinition' | 'benefitOptions'
  >
): boolean {
  return (
    usesBenefitSelection(item) &&
    item.sumInsuredDefinition === 'BENEFIT_SELECTION_WITH_SI'
  );
}

/** Opção de benefício escolhida na linha, pelo código guardado no form. */
export function findBenefitOption(
  item: Pick<CapitalGlobalCoverageCatalogItem, 'benefitOptions'>,
  benefitCode: string | undefined
): CapitalGlobalCoverageBenefitOption | undefined {
  const code = (benefitCode ?? '').trim();
  if (!code) return undefined;
  return (item.benefitOptions ?? []).find(option => option.code === code);
}

/** Coberturas de auxílio mantêm a mesma verba em R$ para sócios e colaboradores. */
export function isAuxilioCoverage(
  item: Pick<CapitalGlobalCoverageCatalogItem, 'category'>
): boolean {
  const category = (item.category ?? '').trim();
  if (!category) return false;

  if (category === AUXILIO_COVERAGE_CATEGORY_CODE) {
    return true;
  }

  return normalizeCategoryLabel(category).includes('auxilio');
}
